import { findIndex } from 'ramda'

import {
  defaultActionGoesToGroup,
  getGroupIdFromDefaultAction,
  getGroupIdFromRule,
  rulePointsToGroup,
} from './treeSaver'

// TODO: cleanup following cutover to routing-api
export const routingRuleIdField = () => 'ruleIdStrings'

export const translateRulesAndGroupsToMagnusNodes = ({
  allGroups,
  allRules,
}) => {
  const nodes = []
  allGroups.forEach((group) => {
    const newNode = makeGroupNodeFromGroup(group, allRules)
    nodes.push(newNode)
  })

  return nodes
}

const makeGroupNodeFromGroup = (group, allRules) => {
  const rules = getRulesForGroup(group, allRules)
  const outputs = getOutputsForRules(rules)
  const defaultRule = makeDefaultRuleForGroup(group)
  const defaultOutput =
    defaultRule.action && defaultRule.action.type === 'EXECUTE_RULE_GROUP'
      ? defaultRule.action.options.nextRuleGroupId
      : 'NONE'

  return {
    id: group.id,
    type: 'group',
    configuration: {
      group,
      rules: [...rules, defaultRule],
      outputs: {
        ...outputs,
        [`group-${group.id}-default`]: { nodeId: defaultOutput },
      },
    },
  }
}

const makeDefaultRuleForGroup = (group) => ({
  outputKey: `group-${group.id}-default`,
  place: group[routingRuleIdField()].length + 1,
  action: group.defaultAction,
  isDefaultForGroup: true,
})

const getRulesForGroup = (group, rules) => {
  return rules
    .filter((rule) => group[routingRuleIdField()].includes(rule.id))
    .map((rule) => ({
      outputKey: rule.id,
      place:
        findIndex((ruleId) => ruleId === rule.id, group[routingRuleIdField()]) +
        1,
      ...rule,
    }))
}

const getOutputsForRules = (rules) => {
  const outputs = {}
  rules.forEach((rule) => {
    const isBranchRule =
      rule.action && rule.action.type === 'EXECUTE_RULE_GROUP'
    if (isBranchRule) {
      outputs[rule.id] = {
        nodeId: rule.action.options.nextRuleGroupId,
      }
    } else {
      outputs[rule.id] = {
        nodeId: `rule-${rule.id}-NONE`,
      }
    }
  })
  return outputs
}

const cleanRuleConditions = (conditionGroup) =>
  conditionGroup.map((conditionSet) =>
    conditionSet.map((condition) => ({
      value: condition.value,
      operator: condition.operator,
      field: condition.field,
    }))
  )

const cleanRule = (rule) => ({
  ...rule,
  conditions: cleanRuleConditions(rule.conditions),
})

export const cleanRules = (rules) => rules.map(cleanRule)

const getGroupIdsPointedToByGroup = (group, allRules) => {
  let groupIds = []

  group[routingRuleIdField()]
    .map((ruleId) => allRules.find((rule) => rule.id === ruleId))
    .filter(rulePointsToGroup)
    .forEach((rule) => (groupIds = [...groupIds, getGroupIdFromRule(rule)]))

  if (defaultActionGoesToGroup(group)) {
    groupIds = [...groupIds, getGroupIdFromDefaultAction(group)]
  }

  return groupIds
}

export const getTreeHasCycle = (
  allRules,
  allRuleGroups,
  defaultRuleGroupId
) => {
  const defaultGroup = allRuleGroups.find(
    (group) => defaultRuleGroupId === group.id
  )
  const groupIdsToVisit = getGroupIdsPointedToByGroup(defaultGroup, allRules)
  return groupIdsToVisit.some((groupId) =>
    groupHasCycle([defaultRuleGroupId], groupId, {
      rules: allRules,
      groups: allRuleGroups,
    })
  )
}

const groupHasCycle = (visitedGroupIds, visitingGroupId, data) => {
  const group = data.groups.find((group) => visitingGroupId === group.id)
  const groupIdsToVisit = getGroupIdsPointedToByGroup(group, data.rules)

  if (
    visitedGroupIds.some((visitedId) => groupIdsToVisit.includes(visitedId))
  ) {
    return true
  }

  if (groupIdsToVisit.length === 0) {
    return false
  }

  return groupIdsToVisit.some((groupId) =>
    groupHasCycle([...visitedGroupIds, visitingGroupId], groupId, data)
  )
}

export const getReachableRulesAndGroups = (
  allRules,
  allRuleGroups,
  defaultRuleGroupId
) => {
  const defaultGroup = allRuleGroups.find(
    (group) => group.id === defaultRuleGroupId
  )
  const rulesInDefaultGroup = defaultGroup[routingRuleIdField()]
    .map((ruleId) => allRules.find((rule) => rule.id === ruleId))
    .filter((r) => !!r)
  let reachableRules = [...rulesInDefaultGroup]
  let reachableGroups = [defaultGroup]
  let visitedGroupIds = [defaultGroup.id]
  const unTraversedRules = rulesInDefaultGroup
  let lookingAtRule = unTraversedRules.shift()

  if (defaultActionGoesToGroup(defaultGroup)) {
    const groupPointedToByGroup = allRuleGroups.find(
      (group) => group.id === getGroupIdFromDefaultAction(defaultGroup)
    )
    if (!visitedGroupIds.includes(groupPointedToByGroup.id)) {
      reachableGroups = [...reachableGroups, groupPointedToByGroup]
    }
    const rulesInNextDefaultGroup = groupPointedToByGroup[
      routingRuleIdField()
    ].map((ruleId) => allRules.find((rule) => rule.id === ruleId))

    unTraversedRules.unshift(...rulesInNextDefaultGroup)
    reachableRules = reachableRules.concat(rulesInNextDefaultGroup)
  }

  const hasId = (id) => (thing) => thing.id === id

  while (lookingAtRule) {
    if (rulePointsToGroup(lookingAtRule)) {
      const nextRuleGroup = allRuleGroups.find(
        hasId(getGroupIdFromRule(lookingAtRule))
      )

      // if we find a cycle, bail on this guy
      if (visitedGroupIds.includes(nextRuleGroup.id)) {
        lookingAtRule = unTraversedRules.shift()
        continue
      }

      visitedGroupIds = [...visitedGroupIds, nextRuleGroup.id]
      reachableGroups = [...reachableGroups, nextRuleGroup]

      if (defaultActionGoesToGroup(nextRuleGroup)) {
        const groupPointedToByGroup = allRuleGroups.find(
          hasId(getGroupIdFromDefaultAction(nextRuleGroup))
        )
        const rulesInNextDefaultGroup = groupPointedToByGroup[
          routingRuleIdField()
        ]
          .map((ruleId) => allRules.find(hasId(ruleId)))
          .filter(Boolean)

        reachableGroups = [...reachableGroups, groupPointedToByGroup]
        unTraversedRules.unshift(...rulesInNextDefaultGroup)
        reachableRules = reachableRules.concat(rulesInNextDefaultGroup)
      }

      const rulesInNextGroup = nextRuleGroup[routingRuleIdField()]
        .map((ruleId) => allRules.find(hasId(ruleId)))
        .filter(Boolean)

      unTraversedRules.unshift(...rulesInNextGroup)
      reachableRules = reachableRules.concat(rulesInNextGroup)
    }

    lookingAtRule = unTraversedRules.shift()
  }

  return { rules: reachableRules, groups: reachableGroups }
}
